import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { useCampaignsActivityQuery } from "modules/campaigns/hooks/useCampaignsActivityQuery"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { ActivityMiniature } from "modules/insights/components/Activity/ActivityMiniature"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignsResponseDataSchema, WeeklyActivityResponseDataSchema } from "schema"
import { z } from "zod"
import { CampaignsSortFields } from "."
import { CampaignTriggerIcon } from "../CampaignTriggerIcon"
import { CampaignFlowBadge } from "../Detail/CampaignFlowBadge"
import { CampaignStatusBadge } from "../Detail/CampaignStatusBadge"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof CampaignsResponseDataSchema>[number]
>()

type CampaignsQueryReturn = ReturnType<typeof useCampaignsQuery>

type Props = {
  campaigns: CampaignsQueryReturn["data"]
  isPending: CampaignsQueryReturn["isPending"]
  fields: CampaignsSortFields[]
  onSort: (field: CampaignsSortFields) => void
  search: {
    field: CampaignsSortFields
    order: "asc" | "desc"
  }
}

export const CampaignsTable = ({ campaigns, isPending, fields, onSort, search }: Props) => {
  const { t } = useTranslation()

  const campaignIds = useMemo(() => campaigns?.map((campaign) => campaign.id), [campaigns])

  const { data: campaignsActivityData, isPending: isCampaignsActivityPending } =
    useCampaignsActivityQuery(campaignIds)

  const campaignsMap = useMemo(() => {
    return campaignsActivityData?.weeklyCounts.reduce((acc, goal) => {
      acc.set(goal.entityId, goal)
      return acc
    }, new Map<string, z.infer<typeof WeeklyActivityResponseDataSchema>["weeklyCounts"][number]>())
  }, [campaignsActivityData])

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("campaigns.table.name"),
          cell: (props) => {
            const name = props.getValue()

            return (
              <span className={cn(!name && "text-muted-foreground")}>
                {name ?? t("campaigns.untitled")}
              </span>
            )
          },
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("status", {
          header: t("campaigns.table.status"),
          cell: (props) => <CampaignStatusBadge status={props.getValue()} />,
          meta: { colSpan: 1 },
        }),
        columnHelper.accessor("flow", {
          header: t("campaigns.table.flow"),
          cell: (props) => {
            const flow = props.getValue()
            if (!flow) return <span className="text-muted-foreground">{"-"}</span>
            return <CampaignFlowBadge flow={flow} />
          },
          meta: { colSpan: 1 },
        }),
        columnHelper.accessor("channel", {
          header: t("campaigns.table.channel"),
          cell: (props) => {
            const channel = props.getValue()
            return <ChannelIcon channel={channel} withTooltip />
          },
          meta: { colSpan: 1, align: "center" },
        }),
        columnHelper.accessor("trigger", {
          header: t("campaigns.table.trigger"),
          cell: (props) => <CampaignTriggerIcon trigger={props.getValue()} emptyValue="-" />,
          meta: { colSpan: 1, align: "center" },
        }),
        columnHelper.accessor("id", {
          header: t("campaigns.goalsManagement.table.activity"),
          cell: (props) => {
            const goalId = props.row.original.id.toString()
            return (
              <ActivityMiniature
                data={campaignsMap?.get(goalId)}
                isPending={isCampaignsActivityPending}
                color="chart-1"
                areaChart
              />
            )
          },
          meta: { colSpan: 1, align: "right" },
        }),
        columnHelper.accessor("createdAt", {
          header: t("campaigns.table.createdAt"),
          cell: (createdAt) => <DateDisplay date={createdAt.getValue()} />,
          meta: { colSpan: 1, align: "right" },
        }),
      ]),
    [t, campaignsMap, isCampaignsActivityPending],
  )

  const table = useTable({
    data: campaigns ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || campaigns === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (campaigns.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/campaigns/$id",
          params: { id: row.original.id },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof CampaignsResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as CampaignsSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as CampaignsSortFields),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
